/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : UsersMultiselect.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { HandleApiActions, projectConfigAPI } from "../../redux/actions";
import { Box, Stack, Typography } from "@mui/material";
import CustomMultiSelectV2 from "../customComponentsV2/common/multiselect/CustomMultiSelectV2";

function UsersMultiselect({
  coOwnerDetails = [],
  editorDetails = [],
  viewerDetails = [],
  coOwners = [],
  setCoOwners,
  editors = [],
  setEditors,
  viewers = [],
  setViewers,
  owner = [],
}) {
  const dispatch = useDispatch();
  const [usersListData, setUsersListData] = useState([]);
  const [fullUsersList, setFullUsersList] = useState([]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const usersList = await Promise.resolve(dispatch(
          HandleApiActions({
            ...projectConfigAPI.get_users_list,
            params: { page_size: 1000, page_number: 1 },
          }),
        ));

        setUsersListData(usersList?.data || []);
        setFullUsersList(usersList?.data || []);
      } catch (error) {
        console.error("Error fetching users", error);
      }
    };
    fetchInitialData();
  }, [dispatch]);

  useEffect(() => {
    const parentCoOwnerDetails = coOwnerDetails?.[0]?.user_id
      ? coOwnerDetails?.map((item) => {
          return {
            label: item.name,
            subLabel: item.email,
            value: item?.user_id,
          };
        })
      : [];

    const parentEditUserDetails = editorDetails?.[0]?.user_id
      ? editorDetails?.map((item) => {
          return {
            label: item.name,
            subLabel: item.email,
            value: item?.user_id,
          };
        })
      : [];

    const parentViewUserDetails = viewerDetails?.[0]?.user_id
      ? viewerDetails?.map((item) => {
          return {
            label: item.name,
            subLabel: item.email,
            value: item?.user_id,
          };
        })
      : [];

    setCoOwners(parentCoOwnerDetails || []);
    setEditors(parentEditUserDetails || []);
    setViewers(parentViewUserDetails || []);
  }, [
    coOwnerDetails,
    editorDetails,
    viewerDetails,
    setCoOwners,
    setEditors,
    setViewers,
  ]);

  useEffect(() => {
    if (!Array.isArray(fullUsersList)) return;

    const selectedUsers = new Set(
      [...coOwners, ...editors, ...viewers, ...owner].map((user) => user.value)
    );

    const unSelectedUserListData = fullUsersList.filter(
      (user) => !selectedUsers.has(user.user_id)
    );

    setUsersListData(unSelectedUserListData);
  }, [coOwners, editors, viewers, owner, fullUsersList]);

  return (
    <Stack spacing={2} mb={2}>
      <CustomMultiSelectV2
        label="Co Owners"
        required={false}
        selectedValues={coOwners}
        setSelectedValues={setCoOwners}
        optionsList={
          Array.isArray(usersListData) && usersListData.length > 0
            ? usersListData.map((item) => ({
                label: item.fullname,
                subLabel: item.email,
                value: item?.user_id,
              }))
            : []
        }
        renderOption={(props, option) => (
          <li {...props} key={option.value}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography sx={{ fontWeight: 400 }}>{option.label}</Typography>
              <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                {option.subLabel}
              </Typography>
            </Box>
          </li>
        )}
        getOptionLabel={(option) => option.label}
      />

      <CustomMultiSelectV2
        label="Editor"
        required={false}
        selectedValues={editors}
        setSelectedValues={setEditors}
        optionsList={
          Array.isArray(usersListData) && usersListData.length > 0
            ? usersListData.map((item) => ({
                label: item.fullname,
                subLabel: item.email,
                value: item?.user_id,
              }))
            : []
        }
        renderOption={(props, option) => (
          <li {...props} key={option.value}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography sx={{ fontWeight: 400 }}>{option.label}</Typography>
              <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                {option.subLabel}
              </Typography>
            </Box>
          </li>
        )}
        getOptionLabel={(option) => option.label}
      />

      <CustomMultiSelectV2
        label="Viewers"
        required={false}
        selectedValues={viewers}
        setSelectedValues={setViewers}
        optionsList={
          Array.isArray(usersListData) && usersListData.length > 0
            ? usersListData.map((item) => ({
                label: item.fullname,
                subLabel: item.email,
                value: item?.user_id,
              }))
            : []
        }
        renderOption={(props, option) => (
          <li {...props} key={option.value}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography sx={{ fontWeight: 400 }}>{option.label}</Typography>
              <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                {option.subLabel}
              </Typography>
            </Box>
          </li>
        )}
        getOptionLabel={(option) => option.label}
      />
    </Stack>
  );
}

UsersMultiselect.propTypes = {
  coOwnerDetails: PropTypes.array,
  editorDetails: PropTypes.array,
  viewerDetails: PropTypes.array,
  coOwners: PropTypes.array,
  setCoOwners: PropTypes.func.isRequired,
  editors: PropTypes.array,
  setEditors: PropTypes.func.isRequired,
  viewers: PropTypes.array,
  setViewers: PropTypes.func.isRequired,
  owner: PropTypes.array,
};

export default UsersMultiselect;
